<template>
  <div :class="['register-uf-view', { 'register-uf-view--loading': isInitializing }]">
    <div
      v-if="isInitializing"
      class="loading"
    >
      <unc-loader />
    </div>

    <div
      v-else-if="showNotFound"
      class="register-card register-uf-notfound rr-notfound-card"
    >
      <div class="rr-notfound-inner">
        <div class="rr-notfound-icon">
          <svg
            aria-hidden="true"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
            />
            <line
              x1="12"
              y1="8"
              x2="12"
              y2="12"
            />
            <line
              x1="12"
              y1="16"
              x2="12.01"
              y2="16"
            />
          </svg>
        </div>
        <h1 class="rr-notfound-title">
          No se pudo cargar la información para crear tu Usuario UNC
        </h1>
        <p class="rr-notfound-desc">
          El enlace utilizado caducó o no es válido. Iniciá nuevamente el proceso de
          creación de Usuario UNC para que volvamos a enviarte el correo.
        </p>
        <button
          type="button"
          class="btn btn-primary waves-effect register-btn register-btn-primary rr-notfound-btn"
          @click.prevent="goRegister()"
        >
          <span>Ir a crear Usuario UNC</span>
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M14 5l7 7m0 0l-7 7m7-7H3"
            />
          </svg>
        </button>
      </div>
    </div>

    <div
      v-else
      class="register-card register-uf-card"
    >
      <div class="register-glow" />

      <div class="card-header ch-alt register-header">
        <div class="register-kicker rr-kicker--info">
          Paso 4 de 4
        </div>
        <h1 class="register-title">
          {{ isCreationSuccess ? `¡Felicitaciones!` : 'Elegí tu Usuario UNC y contraseña' }}
        </h1>
        <p
          v-if="isStepUserName"
          class="register-subtitle"
        >
          Elegí o creá tu nombre de Usuario UNC.
        </p>
        <p
          v-if="isStepPassword && !isSubmissionLocked"
          class="register-subtitle"
        >
          Creá una contraseña segura para tu Usuario UNC. ¡Estás a un paso de finalizar el registro!
        </p>
        <p
          v-else-if="isCreationInProgress"
          class="register-subtitle"
        >
          Estamos creando tu Usuario UNC. Gracias por tu paciencia durante el proceso.
        </p>
        <p
          v-else-if="isCreationSuccess"
          class="register-subtitle"
        >
          ¡Tu Usuario UNC ya está listo!
        </p>
      </div>

      <div class="card-body register-body">
        <form @submit.prevent="validateBeforeSubmit">
          <div
            v-if="isStepUserName"
            class="uf-section"
            style="padding-bottom: 7px;"
          >
            <div class="uf-section-header">
              <span
                id="username-group-label"
                class="uf-section-label"
              >Nombre de usuario</span>
            </div>

            <p class="uf-section-desc">
              Seleccioná una de las opciones disponibles o elegí "Otro" para ingresar un nombre personalizado.
            </p>

            <div
              v-if="isLoading"
              class="uf-loading-inline"
            >
              <unc-loader />
            </div>
            <div
              v-else
              class="rr-person-list"
              role="radiogroup"
              aria-labelledby="username-group-label"
            >
              <label
                v-for="username in optionsUserNames"
                :key="username"
                class="rr-person-card"
                :class="{ 'is-selected': selectedUserName === username }"
              >
                <input
                  v-model="selectedUserName"
                  type="radio"
                  name="userName"
                  :value="username"
                  class="rr-person-radio"
                >
                <div class="uf-username-avatar">
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.8"
                      d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div class="rr-person-info rr-person-info--inline">
                  <span class="rr-person-name">{{ username }}</span>
                  <span class="rr-person-doc">@{{ userDomain }}</span>
                </div>
                <div class="rr-person-check">
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2.5"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </label>

              <label
                class="rr-person-card"
                :class="{ 'is-selected': selectedUserName === 'otro' }"
              >
                <input
                  v-model="selectedUserName"
                  type="radio"
                  name="userName"
                  value="otro"
                  class="rr-person-radio"
                >
                <div class="uf-username-avatar uf-username-avatar--custom">
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.8"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </div>
                <div class="rr-person-info">
                  <span class="rr-person-name">Otro</span>
                  <span
                    class="rr-person-doc"
                    style="font-size: 13px;"
                  >
                    Ingresá un nombre personalizado
                  </span>
                </div>
                <div class="rr-person-check">
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2.5"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </label>

              <transition name="uf-rules-fade">
                <div
                  v-if="isOtroSelected"
                  class="uf-custom-wrapper"
                >
                  <div class="uf-rules-box uf-rules-box--username">
                    <div class="uf-rules-title">
                      Reglas para nombres de Usuario UNC
                    </div>
                    <ul class="uf-rules-list uf-rules-list--password">
                      <li
                        class="uf-rule-item"
                        :class="{
                          'uf-rule-item--neutral': userNameRuleDisplay.displayState === 'neutral',
                          'uf-rule-item--success': userNameRuleDisplay.displayState === 'success',
                          'uf-rule-item--pending': userNameRuleDisplay.displayState === 'pending',
                          'uf-rule-item--info': userNameRuleDisplay.displayState === 'info',
                        }"
                      >
                        <div class="uf-rule-line">
                          <span class="uf-rule-dot" />
                          <div class="uf-rule-text uf-rule-text--username">
                            <span>{{ userNameRuleDisplay.description }}</span>
                            <ul class="uf-rule-sublist">
                              <li
                                v-for="(ruleItem, index) in userNameRuleItems"
                                :key="`user-name-rule-item-${index}`"
                              >
                                {{ ruleItem }}
                              </li>
                            </ul>
                          </div>
                          <span class="uf-rule-status">{{ userNameRuleDisplay.statusText }}</span>
                        </div>
                        <small
                          v-if="userNameRuleDisplay.helperText"
                          class="uf-rule-helper"
                        >{{ userNameRuleDisplay.helperText }}</small>
                      </li>
                    </ul>
                  </div>

                  <div
                    class="form-group register-field uf-custom-input"
                    :class="{ 'has-error': errors.has('userNameOtro') }"
                  >
                    <div class="uf-custom-fg">
                      <label for="userNameOtro">Tu nombre de Usuario UNC</label>
                      <div class="uf-username-input-wrap">
                        <input
                          id="userNameOtro"
                          v-model="userNameOtro"
                          v-validate="'required|userNameValidate|max:50'"
                          name="userNameOtro"
                          type="text"
                          class="form-control"
                          placeholder="Ingresá tu Usuario UNC..."
                          :aria-describedby="errors.has('userNameOtro') ? 'userNameOtro-error' : null"
                        >
                        <span class="uf-domain-suffix">@{{ userDomain }}</span>
                      </div>
                    </div>
                    <small
                      v-if="errors.has('userNameOtro')"
                      id="userNameOtro-error"
                      class="help-block"
                      role="alert"
                      style="margin-bottom: 0;"
                    >{{ errors.first('userNameOtro') }}</small>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <div
            v-if="isStepPassword"
            class="uf-section"
          >
            <div
              v-if="isSubmissionLocked"
              :class="['uf-process-shell', { 'uf-process-shell--success': isCreationSuccess }]"
              aria-live="polite"
              aria-atomic="true"
            >
              <div
                v-if="isCreationInProgress"
                class="uf-process-card"
              >
                <div class="uf-process-loader loading">
                  <unc-loader />
                </div>
                <h2 class="uf-process-title">
                  Estamos creando tu Usuario UNC
                </h2>
                <transition
                  name="uf-process-message"
                  mode="out-in"
                >
                  <p
                    :key="creationMessageIndex"
                    class="uf-process-text"
                  >
                    {{ currentCreationMessage }}
                  </p>
                </transition>
                <div class="uf-process-dots">
                  <span
                    v-for="(message, index) in creationMessages"
                    :key="`creation-step-${index}`"
                    class="uf-process-dot"
                    :class="{ 'is-active': creationMessageIndex === index }"
                  />
                </div>
              </div>

              <div
                v-else-if="isCreationSuccess"
                class="uf-process-card uf-process-card--success"
              >
                <div class="uf-success-icon">
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2.4"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 class="uf-process-title">
                  Tu Usuario UNC <span class="uf-process-user-name">{{ item.userName + '@' + userDomain }}</span> fue creado con éxito
                </h2>
                <button
                  v-if="configApp && configApp.urlProfile"
                  type="button"
                  class="btn btn-primary waves-effect register-btn register-btn-primary uf-success-btn"
                  @click.prevent="goProfile()"
                >
                  <span>Ir a MiUNC</span>
                  <svg
                    aria-hidden="true"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    <polyline points="10 17 15 12 10 7" />
                    <line
                      x1="15"
                      y1="12"
                      x2="3"
                      y2="12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <template v-else>
              <div
                v-if="hasPasswordPolicyRules"
                class="uf-rules-box"
              >
                <div class="uf-rules-title">
                  Reglas para contraseñas
                </div>
                <ul class="uf-rules-list uf-rules-list--password">
                  <li
                    v-for="rule in passwordRulesDisplay"
                    :key="rule.key"
                    class="uf-rule-item"
                    :class="{
                      'uf-rule-item--neutral': rule.displayState === 'neutral',
                      'uf-rule-item--success': rule.displayState === 'success',
                      'uf-rule-item--pending': rule.displayState === 'pending',
                      'uf-rule-item--info': rule.displayState === 'info',
                    }"
                  >
                    <div class="uf-rule-line">
                      <span class="uf-rule-dot" />
                      <span class="uf-rule-text">{{ rule.description }}</span>
                      <span class="uf-rule-status">{{ rule.statusText }}</span>
                    </div>
                    <small
                      v-if="rule.helperText"
                      class="uf-rule-helper"
                    >{{ rule.helperText }}</small>
                  </li>
                </ul>
              </div>

              <div
                class="form-group register-field"
                :class="{ 'has-error': errors.has('password') }"
              >
                <div>
                  <label for="password">Ingresá tu contraseña</label>
                  <div class="uf-password-wrap">
                    <input
                      id="password"
                      v-model="item.password"
                      v-validate="passwordValidationRules"
                      :type="passwordFieldType"
                      name="password"
                      class="form-control"
                      placeholder="Ingresá una contraseña válida..."
                      autocomplete="new-password"
                      :aria-describedby="errors.has('password') ? 'password-error' : null"
                      @keyup="showValidationErrorPasswordConfirmation = false"
                    >
                    <button
                      type="button"
                      class="uf-eye-btn"
                      :aria-label="passwordFieldType === 'password' ? 'Mostrar contraseña' : 'Ocultar contraseña'"
                      :disabled="isSubmissionLocked"
                      @click.prevent="showPassword(passwordFieldType === 'password', false)"
                    >
                      <svg
                        v-if="passwordFieldType === 'password'"
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <svg
                        v-else
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <small
                  v-if="errors.has('password')"
                  id="password-error"
                  class="help-block"
                  role="alert"
                >{{ errors.first('password') }}</small>
              </div>

              <div
                class="form-group register-field"
                :class="{ 'has-error': errors.has('passwordConfirmation') || showValidationErrorPasswordConfirmation }"
              >
                <div>
                  <label for="passwordConfirmation">Repetir contraseña</label>
                  <div class="uf-password-wrap">
                    <input
                      id="passwordConfirmation"
                      v-model="item.passwordConfirmation"
                      v-validate="'required'"
                      :type="passwordConfirmationFieldType"
                      name="passwordConfirmation"
                      class="form-control"
                      placeholder="Ingresá nuevamente tu contraseña..."
                      autocomplete="new-password"
                      :aria-describedby="errors.has('passwordConfirmation') ? 'passwordConfirmation-error' : null"
                      @keyup="showValidationErrorPasswordConfirmation = false"
                    >
                    <button
                      type="button"
                      class="uf-eye-btn"
                      :aria-label="passwordConfirmationFieldType === 'password' ? 'Mostrar contraseña' : 'Ocultar contraseña'"
                      :disabled="isSubmissionLocked"
                      @click.prevent="showPassword(passwordConfirmationFieldType === 'password', true)"
                    >
                      <svg
                        v-if="passwordConfirmationFieldType === 'password'"
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <svg
                        v-else
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <small
                  v-if="errors.has('passwordConfirmation')"
                  id="passwordConfirmation-error"
                  class="help-block"
                  role="alert"
                >{{
                  errors.first('passwordConfirmation') }}</small>
                <small
                  v-if="showValidationErrorPasswordConfirmation"
                  class="help-block"
                >Las contraseñas no coinciden</small>
              </div>
            </template>
          </div>

          <div class="rr-actions">
            <button
              v-if="isStepPassword && !isSubmissionLocked"
              type="button"
              class="btn btn-link waves-effect register-btn register-btn-secondary"
              @click.prevent="goPreviousStep()"
            >
              Volver
            </button>
            <button
              v-if="isStepUserName"
              type="button"
              class="btn btn-primary waves-effect register-btn register-btn-primary"
              :disabled="isSubmissionLocked"
              @click.prevent="goNextStep()"
            >
              <span>Siguiente</span>
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </button>
            <button
              v-else-if="isStepPassword && !isSubmissionLocked"
              type="submit"
              class="btn btn-primary waves-effect register-btn register-btn-primary"
              :disabled="isSubmissionLocked"
            >
              <span>Crear mi Usuario UNC</span>
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import validateForm from '../../mixins/validate-form';
import {
    HttpFRONT,
    HttpYULI,
    swal,
} from '../../share.js';

const CREATION_MIN_DURATION_MS = 2000;
const CREATION_MESSAGE_INTERVAL_MS = 1000;
const CREATION_MESSAGES = [
    'Validando la información ingresada...',
    '¡Ya casi terminamos!',
];

export default {
    name: 'RegisterUserForm',
    mixins: [validateForm],
    props: {
        token: {
            type: String,
            default: '',
        },
    },
    data () {
        return {
            isInitializing: true,
            isLoading: true,
            item: {
                userName: '',
                password: '',
                passwordConfirmation: '',
            },
            person: null,
            selectedUserName: '',
            userNameOtro: '',
            optionsUserNames: null,
            showNotFound: false,
            showValidationErrorPasswordConfirmation: false,
            passwordFieldType: 'password',
            passwordConfirmationFieldType: 'password',
            currentStep: 1,
            userDomain: '',
            passwordPolicy: null,
            backendRuleErrors: [],
            userNameRuleState: 'info', // 'info' | 'pending' | 'success'
            userNameRuleErrorMessage: '',
            passwordPolicyState: 'loading', // 'loading' | 'ready' | 'error'
            creationFlowState: 'idle', // 'idle' | 'creating' | 'success'
            creationFlowStartedAt: 0,
            creationMessageIndex: 0,
            creationMessageIntervalId: null,
        };
    },
    computed: {
        ...mapGetters([
            'configApp',
        ]),
        hasPasswordPolicyRules () {
            return Array.isArray(this.passwordPolicy?.rules) && this.passwordPolicy.rules.length > 0;
        },
        canContinue () {
            return this.passwordPolicyState === 'ready';
        },
        isStepUserName () {
            return this.currentStep === 1;
        },
        isStepPassword () {
            return this.currentStep === 2;
        },
        isCreationInProgress () {
            return this.creationFlowState === 'creating';
        },
        isCreationSuccess () {
            return this.creationFlowState === 'success';
        },
        isSubmissionLocked () {
            return this.isCreationInProgress || this.isCreationSuccess;
        },
        creationMessages () {
            return CREATION_MESSAGES;
        },
        currentCreationMessage () {
            return this.creationMessages[this.creationMessageIndex] || this.creationMessages[0];
        },
        isUserNameStepReady () {
            if (this.isLoading || !this.selectedUserName) {
                return false;
            }

            if (this.isOtroSelected) {
                return this.userNameOtro.trim().length > 0;
            }

            return true;
        },
        passwordValidationRules () {
            if (this.passwordPolicyState !== 'ready') return '';

            const rules = ['required'];
            this.passwordPolicy?.rules?.forEach(rule => {
                if (rule.frontendValidation && rule.regex) {
                    rules.push(`password-${rule.name}`);
                }
            });

            return rules.join('|');
        },
        passwordRulesDisplay () {
            const rules = this.passwordPolicy?.rules;

            if (!Array.isArray(rules) || rules.length === 0) {
                return [];
            }

            const currentPassword = this.item.password || '';
            const hasPasswordInput = currentPassword.length > 0;
            const hasBackendRuleEvaluation = hasPasswordInput &&
                Array.isArray(this.backendRuleErrors) &&
                this.backendRuleErrors.length > 0;

            return rules.map((rule, index) => {
                const description = rule?.description || rule?.error || rule?.name || 'Regla de contraseña';
                const backendFailureMessage = hasPasswordInput
                    ? this.getBackendRuleFailureMessage(rule)
                    : '';
                const isFrontendRule = Boolean(rule?.frontendValidation && rule?.regex);

                if (hasBackendRuleEvaluation) {
                    if (backendFailureMessage) {
                        return {
                            key: `${rule?.name || 'rule'}-${index}`,
                            description,
                            displayState: 'pending',
                            statusText: 'No cumple',
                            helperText: 'La contraseña no cumple esta regla.',
                        };
                    }

                    return {
                        key: `${rule?.name || 'rule'}-${index}`,
                        description,
                        displayState: 'success',
                        statusText: 'Cumple',
                        helperText: '',
                    };
                }

                if (!isFrontendRule) {
                    return {
                        key: `${rule?.name || 'rule'}-${index}`,
                        description,
                        displayState: 'info',
                        statusText: 'Se valida al crear',
                        helperText: 'Esto lo validaremos cuando aprietes el botón "Crear mi Usuario UNC".',
                    };
                }

                const { validRegex, matches } = this.testPasswordRuleRegex(rule.regex, currentPassword);

                if (!validRegex) {
                    return {
                        key: `${rule?.name || 'rule'}-${index}`,
                        description,
                        displayState: 'info',
                        statusText: 'Se valida al crear',
                        helperText: 'No pudimos evaluar esta regla en el formulario. La validaremos al crear tu Usuario UNC.',
                    };
                }

                if (!hasPasswordInput) {
                    return {
                        key: `${rule?.name || 'rule'}-${index}`,
                        description,
                        displayState: 'neutral',
                        statusText: 'Pendiente',
                        helperText: '',
                    };
                }

                if (matches) {
                    return {
                        key: `${rule?.name || 'rule'}-${index}`,
                        description,
                        displayState: 'success',
                        statusText: 'Cumple',
                        helperText: '',
                    };
                }

                return {
                    key: `${rule?.name || 'rule'}-${index}`,
                    description,
                    displayState: 'pending',
                    statusText: 'Falta cumplir',
                    helperText: '',
                };
            });
        },
        userNameRuleItems () {
            return [
                'Iniciar con una o más letras consecutivas de alguno de tus nombres.',
                'Luego, seguir con alguno de tus apellidos completo.',
                'Opcional: Puede terminar con uno o más números.',
                'Opcional: Podés usar _ (guion bajo), - (guion medio) o . (punto) como separadores.',
                'No se admite ningún otro caracter más allá de letras minúsculas, números o los separadores mencionados. Por ejemplo, no se permiten espacios ni caracteres especiales como @, #, $, etc.',
            ];
        },
        userNameRuleDisplay () {
            const description = 'El nombre de Usuario UNC debe cumplir con todas estas condiciones:';

            if (this.userNameRuleState === 'pending') {
                return {
                    description,
                    displayState: 'pending',
                    statusText: 'No cumple',
                    helperText: this.userNameRuleErrorMessage || 'El nombre de Usuario UNC no cumple las reglas definidas.',
                };
            }

            if (this.userNameRuleState === 'success') {
                return {
                    description,
                    displayState: 'success',
                    statusText: 'Cumple',
                    helperText: '',
                };
            }

            return {
                description,
                displayState: 'info',
                statusText: 'Se valida al crear',
                helperText: 'Validaremos estas reglas cuando aprietes el botón "Crear mi Usuario UNC" en la siguiente pantalla.',
            };
        },
        isOtroSelected () {
            return this.selectedUserName === 'otro';
        },
    },
    watch: {
        selectedUserName (newValue) {
            if (newValue !== 'otro') {
                this.userNameOtro = '';
            }

            this.resetUserNameRuleFeedback();
        },
        'item.password' () {
            this.backendRuleErrors = [];
        },
        userNameOtro () {
            this.resetUserNameRuleFeedback();
        },
    },
    mounted () {
        if (this.token) {
            this.getData();
            this.getPasswordPolicy();
        } else {
            this.showNotFound = true;
            this.isInitializing = false;
        }
    },
    beforeDestroy () {
        this.stopCreationMessageRotation();
    },
    methods: {
        wait (ms) {
            return new Promise(resolve => {
                setTimeout(resolve, ms);
            });
        },
        startCreationMessageRotation () {
            this.stopCreationMessageRotation();

            this.creationMessageIntervalId = setInterval(() => {
                this.creationMessageIndex = (this.creationMessageIndex + 1) % this.creationMessages.length;
            }, CREATION_MESSAGE_INTERVAL_MS);
        },
        stopCreationMessageRotation () {
            if (this.creationMessageIntervalId) {
                clearInterval(this.creationMessageIntervalId);
                this.creationMessageIntervalId = null;
            }
        },
        startCreationFlow () {
            this.creationFlowState = 'creating';
            this.creationFlowStartedAt = Date.now();
            this.creationMessageIndex = 0;
            this.startCreationMessageRotation();
        },
        async waitForCreationMinimumDuration () {
            const elapsed = Date.now() - this.creationFlowStartedAt;
            const remaining = CREATION_MIN_DURATION_MS - elapsed;

            if (remaining > 0) {
                await this.wait(remaining);
            }
        },
        stopCreationFlow () {
            this.stopCreationMessageRotation();
            this.creationFlowState = 'idle';
            this.creationFlowStartedAt = 0;
        },
        async completeCreationSuccess () {
            this.stopCreationMessageRotation();
            this.creationFlowState = 'success';
        },
        async getData () {
            try {
                const response = await HttpYULI.get(`/v2/people?token=${this.token}`);

                if (response) {
                    this.person = response.data[0];
                    if (this.person._id && (this.person.state.code === 'V' || this.person.state.code === 'PE')) {
                        // El token existe, el _id existe, y el estado es Valid
                        const personId = this.person._id;
                        this.userDomain = this.person.profile.domain;
                        this.isLoading = true;

                        const responseUsernames = await this.getUserNames(personId);
                        if (responseUsernames.data) {
                            this.optionsUserNames = responseUsernames.data;
                        } else {
                            this.showNotFound = true;
                        }
                        this.isLoading = false;
                    } else {
                        this.showNotFound = true;
                    }
                } else {
                    this.showNotFound = true;
                }
            } catch (error) {
                this.showNotFound = true;
            } finally {
                this.isInitializing = false;
            }
        },
        getUserNames (personId) {
            return HttpYULI.get(`/v2/people/${personId}/usernames`);
        },
        testPasswordRuleRegex (regexPattern, passwordValue) {
            try {
                const regex = new RegExp(regexPattern);
                return {
                    validRegex: true,
                    matches: regex.test(passwordValue),
                };
            } catch {
                return {
                    validRegex: false,
                    matches: false,
                };
            }
        },
        normalizeRuleText (text = '') {
            return String(text)
                .toLowerCase()
                .trim();
        },
        resetUserNameRuleFeedback () {
            this.userNameRuleState = 'info';
            this.userNameRuleErrorMessage = '';
        },
        getApiErrorMessages (responseData) {
            const messages = [];

            if (typeof responseData === 'string') {
                messages.push(responseData);
            }

            if (responseData && typeof responseData.message === 'string') {
                messages.push(responseData.message);
            }

            const details = responseData && responseData.details;
            const list = Array.isArray(details) ? details : (details ? [details] : []);

            list.forEach(detail => {
                if (detail && typeof detail.message === 'string') {
                    messages.push(detail.message);
                }
            });

            return messages;
        },
        getUserNameRuleErrorMessage (responseData) {
            const details = responseData && responseData.details;
            const list = Array.isArray(details) ? details : (details ? [details] : []);
            const messages = this.getApiErrorMessages(responseData);

            const userNameMessage = messages.find(message => {
                const normalized = this.normalizeRuleText(message);
                const hasUserNameKeyword = normalized.includes('usuario') || normalized.includes('nombre de usuario') || normalized.includes('username');
                const hasPasswordKeyword = normalized.includes('contrasena') || normalized.includes('contraseña') || normalized.includes('password');

                return hasUserNameKeyword && !hasPasswordKeyword;
            });

            if (userNameMessage) {
                return userNameMessage;
            }

            if (list.length === 0 && typeof responseData?.message === 'string') {
                return responseData.message;
            }

            return '';
        },
        getBackendRuleFailureMessage (rule) {
            if (!Array.isArray(this.backendRuleErrors) || this.backendRuleErrors.length === 0) {
                return '';
            }

            const ruleName = this.normalizeRuleText(rule?.name || '');
            const ruleError = this.normalizeRuleText(rule?.error || '');

            const matchedError = this.backendRuleErrors.find(detail => {
                const detailName = this.normalizeRuleText(detail?.name || '');
                const detailMessage = this.normalizeRuleText(detail?.message || '');

                if (detailName && ruleName && detailName === ruleName) {
                    return true;
                }

                if (detailMessage && ruleError) {
                    return detailMessage === ruleError || detailMessage.includes(ruleError) || ruleError.includes(detailMessage);
                }

                return false;
            });

            return matchedError?.message || '';
        },
        async getPasswordPolicy () {
            this.passwordPolicyState = 'loading';

            try {
                const response = await HttpYULI.get('/v2/auth/password-policy');
                this.passwordPolicy = response.data;

                // TEMPORARY: the API should NOT return the "password reused" rule here.
                const isPasswordReusedRule = (rule) => {
                    const name = String(rule?.name || '').toLowerCase();

                    return (
                        name === 'password-reused' ||
                        name.includes('password-reused')
                    );
                };

                if (Array.isArray(this.passwordPolicy?.rules)) {
                    this.passwordPolicy = {
                        ...this.passwordPolicy,
                        rules: this.passwordPolicy.rules.filter(rule => !isPasswordReusedRule(rule)),
                    };
                }

                this.passwordPolicy.rules?.forEach(rule => {
                    if (rule.frontendValidation && rule.regex) {
                        const validatorName = `password-${rule.name}`;
                        const regex = new RegExp(rule.regex);

                        this.$validator.extend(validatorName, {
                            getMessage: () => rule.error,
                            validate: (value) => regex.test(value),
                        });
                    }
                });
                this.passwordPolicyState = 'ready';
            } catch (error) {
                this.passwordPolicy = null;
                this.passwordPolicyState = 'error';
                swal({
                    title: 'No pudimos cargar las reglas de seguridad',
                    text: 'No pudimos obtener las reglas de seguridad para contraseñas. Por favor, intentá nuevamente más tarde.',
                    type: 'warning',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                });
            }
        },
        async validateBeforeSubmit () {
            if (this.isSubmissionLocked) {
                return;
            }

            if (this.isStepUserName) {
                await this.goNextStep();
                return;
            }

            if (!this.canContinue) {
                swal({
                    title: 'No pudimos cargar las reglas de seguridad',
                    text: 'No pudimos obtener las reglas de seguridad para contraseñas. Por favor, intentá nuevamente más tarde.',
                    type: 'warning',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                });
                return;
            }

            try {
                if (await this.validateForm()) {
                    if (
                        this.validatePasswordConfirmation(
                            this.item.password,
                            this.item.passwordConfirmation,
                        )
                    ) {
                        await this.save();
                    } else {
                        this.showValidationErrorPasswordConfirmation = true;
                    }
                }
            } catch {
                this.stopCreationFlow();
                swal({
                    title: 'Algo salió mal',
                    text: 'Sus datos no fueron registrados.',
                    type: 'warning',
                });
            }
        },
        async goNextStep () {
            if (this.isSubmissionLocked) {
                return;
            }

            if (this.isLoading) {
                swal({
                    title: 'Todavia estamos preparando esta etapa',
                    text: 'Esperá un momento mientras cargamos las opciones de nombre de usuario.',
                    type: 'warning',
                });
                return;
            }

            if (!this.selectedUserName) {
                swal({
                    title: 'Elegí un nombre de usuario',
                    text: 'Seleccioná una de las opciones disponibles para continuar.',
                    type: 'warning',
                });
                return;
            }

            if (this.isOtroSelected && this.userNameOtro.trim().length === 0) {
                swal({
                    title: 'Completá tu nombre de usuario',
                    text: 'Ingresá el nombre de usuario personalizado que querés utilizar.',
                    type: 'warning',
                });
                return;
            }

            if (this.isOtroSelected) {
                const isValidCustomUserName = await this.$validator.validate('userNameOtro');
                if (!isValidCustomUserName) {
                    swal({
                        title: 'Revisá el nombre de usuario ingresado',
                        text: 'Corregí el nombre de usuario personalizado para poder continuar.',
                        type: 'warning',
                    });
                    return;
                }
            }
            this.currentStep = 2;
            this.showValidationErrorPasswordConfirmation = false;
        },
        goPreviousStep () {
            if (this.isSubmissionLocked) {
                return;
            }

            this.currentStep = 1;
            this.showValidationErrorPasswordConfirmation = false;
        },
        validatePasswordConfirmation (password, passwordConfirmation) {
            return password === passwordConfirmation;
        },
        async save () {
            if (this.isSubmissionLocked) {
                return;
            }

            this.item.userName = this.isOtroSelected
                ? this.userNameOtro.toLowerCase()
                : this.selectedUserName;

            const userToSend = {
                person: this.person._id,
                userName: this.item.userName,
                password: this.item.password,
                token: this.token,
            };

            this.startCreationFlow();

            try {
                const response = await HttpFRONT.post('usuarios', userToSend);
                await this.waitForCreationMinimumDuration();

                if (response.status === 201 || response.status === 200) {
                    if (this.isOtroSelected) {
                        this.userNameRuleState = 'success';
                        this.userNameRuleErrorMessage = '';
                    }

                    await this.completeCreationSuccess();
                }
            } catch (error) {
                this.stopCreationFlow();

                const status = error?.response?.status;
                if (status === 400) {
                    swal({
                        title: 'Tu Usuario UNC no fue creado',
                        text: 'Faltan campos requeridos.',
                        type: 'warning',
                    });
                } else if (status === 401) {
                    swal({
                        title: 'Acceso denegado',
                        text: 'No posee permisos para acceder a este servicio.',
                        type: 'warning',
                    });
                } else if (status === 422) {
                    const data = (error.response && error.response.data) || {};
                    const details = data.details;
                    const list = Array.isArray(details) ? details : (details ? [details] : []);
                    this.backendRuleErrors = list;
                    const userNameErrorMessage = this.isOtroSelected
                        ? this.getUserNameRuleErrorMessage(data)
                        : '';

                    if (this.isOtroSelected) {
                        const hasUserNameError = Boolean(userNameErrorMessage);

                        this.userNameRuleState = hasUserNameError ? 'pending' : 'success';
                        this.userNameRuleErrorMessage = hasUserNameError ? userNameErrorMessage : '';
                    }

                    const messages = list
                        .map(d => d && d.message)
                        .filter(Boolean);

                    const text = messages.length > 0
                        ? messages.join('\n')
                        : (userNameErrorMessage || 'Tu contraseña no cumple con las políticas de seguridad.');

                    swal({
                        title: 'No pudimos crear tu Usuario UNC',
                        text,
                        type: 'warning',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    });
                } else if (status === 409) {
                    if (this.isOtroSelected) {
                        this.userNameRuleState = 'pending';
                        this.userNameRuleErrorMessage = 'El nombre de Usuario UNC no cumple las reglas o ya está en uso.';

                        this.goPreviousStep();
                    }

                    swal({
                        title: 'Tu Usuario UNC no fue creado',
                        text: 'El nombre de Usuario UNC no cumple las reglas o ya está en uso.',
                        type: 'warning',
                    });
                } else {
                    swal({
                        title: 'Algo salió mal',
                        text: 'Servicio temporalmente no disponible.',
                        type: 'warning',
                    });
                }
            }
        },
        goRegister () {
            this.$router.push('/registro');
        },
        goProfile () {
            const urlProfile = this.configApp?.urlProfile || '';

            if (!urlProfile) {
                return;
            }

            window.open(urlProfile, '_self', 'noopener,noreferrer');
        },
        showPassword (show = true, confirmation = false) {
            if (this.isSubmissionLocked) {
                return;
            }

            const fieldType = show ? 'text' : 'password';

            if (confirmation) {
                this.passwordConfirmationFieldType = fieldType;
            } else {
                this.passwordFieldType = fieldType;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import './register-shared.scss';

.register-uf-view {
  --register-azul: #003865;
  --register-azul-deep: #001f3f;
  --register-azul-mid: #0a5f96;
  --register-verde: #3eb6b8;
  --register-ink: #102238;
  --register-muted: #526579;
  --register-border: rgba(0, 56, 101, 0.14);

  padding: 32px 16px 0;
}

.register-uf-view--loading {
  padding-top: 0;
}

.register-uf-notfound {
  width: 100%;
  max-width: $register-card-wide;
  margin: 0 auto;
}

.register-uf-card {
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
}

.uf-section {
  margin-bottom: 17px;
  padding-bottom: 0;
  border-bottom: none;
  padding-bottom: 0px !important;
}

.uf-section:last-of-type {
  border-bottom: none;
  margin-bottom: 8px;
  padding-bottom: 0;
}

.uf-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  gap: 12px;
}

.uf-section-label {
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.3px;
  color: var(--register-azul-mid);
}

.uf-section-desc {
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--register-muted);
  line-height: 1.65;
  margin-bottom: 16px;
}

.register-field {
  margin-bottom: 18px;
}

.register-field.has-error label {
  color: #cf425c;
}

.uf-rules-box {
  background: rgba(10, 95, 150, 0.04);
  border: 1px solid rgba(10, 95, 150, 0.12);
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.uf-rules-title {
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: var(--register-azul-mid);
  margin-bottom: 8px;
}

.uf-rules-desc {
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--register-muted);
  line-height: 1.6;
  margin-bottom: 8px;
}

.uf-rules-desc code {
  background: rgba(0, 56, 101, 0.07);
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 11px;
}

.uf-rules-list {
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--register-muted);
  line-height: 1.65;
  padding-left: 18px;
  margin-bottom: 8px;
}

.uf-rules-list--password {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.uf-rule-item {
  border: 1px solid rgba(10, 95, 150, 0.15);
  border-radius: 10px;
  padding: 6px 8px;
  background: rgba(10, 95, 150, 0.04);
}

.uf-rule-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.uf-rule-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
  background: #7f91a5;
}

.uf-rule-text {
  flex: 1;
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.55;
}

.uf-rule-text--username {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.uf-rule-sublist {
  margin: 0;
  padding-left: 16px;
  list-style: disc;
}

.uf-rule-sublist li {
  font-size: 12px;
  line-height: 1.45;
  margin-bottom: 2px;
}

.uf-rule-sublist li:last-child {
  margin-bottom: 0;
}

.uf-rule-status {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.uf-rule-helper {
  display: block;
  margin-top: 6px;
  margin-left: 16px;
  font-family: var(--font-body);
  font-size: 12px;
  line-height: 1.5;
  color: #5a6d82;
}

.uf-rule-item--neutral {
  border-color: rgba(86, 107, 132, 0.25);
  background: rgba(86, 107, 132, 0.07);
  color: #3f556f;
}

.uf-rule-item--neutral .uf-rule-dot {
  background: #6f8196;
}

.uf-rule-item--neutral .uf-rule-status {
  color: #5c6f84;
}

.uf-rule-item--success {
  border-color: rgba(46, 141, 89, 0.36);
  background: rgba(46, 141, 89, 0.08);
  color: #1f5d3c;
}

.uf-rule-item--success .uf-rule-dot {
  background: #2e8d59;
}

.uf-rule-item--success .uf-rule-status {
  color: #2a7f50;
}

.uf-rule-item--pending {
  border-color: rgba(218, 128, 18, 0.42);
  background: rgba(218, 128, 18, 0.09);
  color: #8a4f06;
}

.uf-rule-item--pending .uf-rule-dot {
  background: #d18314;
}

.uf-rule-item--pending .uf-rule-status {
  color: #b7680d;
}

.uf-rule-item--info {
  border-color: rgba(26, 116, 162, 0.3);
  background: rgba(26, 116, 162, 0.08);
  color: #1c5f86;
}

.uf-rule-item--info .uf-rule-dot {
  background: #1a74a2;
}

.uf-rule-item--info .uf-rule-status {
  color: #206992;
}

.uf-rules-fade-enter-active,
.uf-rules-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.uf-rules-fade-enter,
.uf-rules-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.uf-username-avatar {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--register-azul), var(--register-azul-mid));
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.22s ease, color 0.22s ease;
}

.uf-username-avatar svg {
  width: 22px;
  height: 22px;
}

.uf-username-avatar--custom {
  background: linear-gradient(135deg, var(--register-azul), var(--register-azul-mid));
  color: #ffffff;
}

.rr-person-card.is-selected .uf-username-avatar {
  background: linear-gradient(135deg, var(--register-azul-mid), var(--register-verde));
  color: white;
}

.rr-person-info--inline {
  flex-direction: row;
  align-items: baseline;
  gap: 1px;
  flex-wrap: nowrap;
}

.rr-person-info--inline .rr-person-name,
.rr-person-info--inline .rr-person-doc {
  white-space: nowrap;
}

.rr-person-info--inline .rr-person-name {
  font-size: 15px;
}

.rr-person-info--inline .rr-person-doc {
  font-size: 15px;
}

.uf-loading-inline {
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.uf-custom-input {
  margin-top: 10px;
  padding: 16px 18px;
  background: rgba(246, 250, 253, 0.9);
  border: 1px solid rgba(62, 182, 184, 0.3);
  border-radius: 14px;
}

.uf-rules-box--username {
  margin-bottom: 10px;
}

.uf-custom-fg label {
  display: block;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.3px;
  color: var(--register-azul-mid);
  margin-bottom: 10px;
}

.uf-username-input-wrap {
  display: flex;
  align-items: center;
  gap: 0;
}

.uf-username-input-wrap .form-control {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-right: none !important;
  flex: 1;
}

.uf-domain-suffix {
  display: inline-flex;
  align-items: center;
  height: 42px;
  padding: 0 14px;
  background: rgba(0, 56, 101, 0.05);
  border: 1px solid rgba(0, 56, 101, 0.16);
  border-left: none;
  border-radius: 0 12px 12px 0;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--register-muted);
  white-space: nowrap;
  flex-shrink: 0;
}

.uf-password-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.uf-password-wrap .form-control {
  padding-right: 48px !important;
}

.uf-eye-btn {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 56, 101, 0.06);
  color: var(--register-azul-mid);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease;
  cursor: pointer;
}

.uf-eye-btn:hover {
  background: rgba(62, 182, 184, 0.14);
  color: var(--register-azul);
}

.uf-eye-btn:focus {
  outline: none;
}

.uf-eye-btn svg {
  width: 18px;
  height: 18px;
}

.uf-eye-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.uf-process-shell {
  border-radius: 16px;
  border: 1px solid rgba(10, 95, 150, 0.18);
  background: radial-gradient(circle at 18% 18%, rgba(62, 182, 184, 0.15), transparent 44%),
    linear-gradient(145deg, rgba(0, 56, 101, 0.08), rgba(10, 95, 150, 0.05));
  padding: 20px 22px;
}

.uf-process-shell--success {
  border: none;
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.uf-process-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.uf-process-loader {
  min-height: auto;
  margin: 0 0 12px;
}

.uf-process-title {
  margin: 0;
  margin-top: 20px;
  font-family: var(--font-body);
  font-size: 22px;
  font-weight: 700;
  color: var(--register-azul-deep);
  letter-spacing: 1.2px;
}

.uf-process-text {
  margin: 11px 0 0;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.6;
  color: var(--register-ink);
}

.uf-process-dots {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.uf-process-dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: rgba(16, 34, 56, 0.26);
  transition: transform 0.2s ease, background 0.2s ease;
}

.uf-process-dot.is-active {
  transform: scale(1.2);
  background: var(--register-verde);
}

.uf-process-card--success {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(42, 127, 80, 0.24);
  border-radius: 14px;
  padding: 24px 22px;
  background: radial-gradient(circle at 18% 18%, rgba(42, 127, 80, 0.16), transparent 44%),
    linear-gradient(145deg, rgba(42, 127, 80, 0.12), rgba(31, 93, 60, 0.06));
  box-shadow: none;
}

.uf-process-card--success::before,
.uf-process-card--success::after {
  content: '';
  position: absolute;
  pointer-events: none;
}

.uf-process-card--success::before {
  left: 50%;
  top: 42px;
  width: 190%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(42, 127, 80, 0.22) 0%, rgba(42, 127, 80, 0.15) 38%, rgba(42, 127, 80, 0.1) 64%, rgba(42, 127, 80, 0.08) 100%);
  transform: translate(-50%, -50%) scale(0);
  transform-origin: 50% 50%;
  will-change: transform;
  z-index: 0;
  animation: uf-success-fill 2s ease-out forwards;
}

.uf-process-card--success::after {
  left: 50%;
  top: 42px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(42, 127, 80, 0.28) 0%, rgba(42, 127, 80, 0.1) 60%, rgba(42, 127, 80, 0) 78%);
  transform: translate(-50%, -50%);
  opacity: 0.38;
  z-index: 0;
}

.uf-process-card--success .uf-process-text {
  color: #1f5d3c;
}

.uf-process-card--success .uf-success-icon,
.uf-process-card--success .uf-process-title,
.uf-process-card--success .uf-process-text,
.uf-process-card--success .uf-success-btn {
  position: relative;
  z-index: 1;
}

.uf-success-icon {
  width: 56px;
  height: 56px;
  position: relative;
  overflow: visible;
  border-radius: 50%;
  background: linear-gradient(140deg, #2a7f50, #1f5d3c);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 24px rgba(42, 127, 80, 0.32);
  animation: uf-success-pop 0.45s ease-out;
}

.uf-success-icon svg {
  width: 28px;
  height: 28px;
}

.uf-success-btn {
  margin-top: 18px;
}

.uf-process-message-enter-active,
.uf-process-message-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.uf-process-message-enter,
.uf-process-message-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

.uf-process-user-name {
  color: #1f5d3c;
}

@keyframes uf-success-pop {
  0% {
    transform: scale(0.76);
    opacity: 0;
  }
  75% {
    transform: scale(1.06);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes uf-success-fill {
  0% {
    transform: translate(-50%, -50%) scale(0);
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .uf-process-card--success::before {
    animation: none;
    transform: translate(-50%, -50%) scale(1);
  }

  .uf-success-icon {
    animation: none;
  }

  .uf-process-message-enter-active,
  .uf-process-message-leave-active,
  .uf-process-dot {
    transition: none;
  }
}

@media (max-width: 600px) {
  .register-uf-view {
    padding: 24px 12px 0;
  }

  .uf-section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .rr-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }

  .rr-actions .register-btn {
    width: 100%;
    justify-content: center;
  }

  .uf-username-input-wrap {
    flex-direction: column;
    align-items: stretch;
  }

  .uf-username-input-wrap .form-control {
    border-radius: 12px !important;
    border-right: 1px solid rgba(0, 56, 101, 0.16) !important;
  }

  .uf-domain-suffix {
    width: 100%;
    border-radius: 0 0 12px 12px;
    border-top: none;
    border-left: 1px solid rgba(0, 56, 101, 0.16);
    justify-content: center;
    height: 36px;
  }

  .uf-process-shell {
    padding: 22px 14px;
  }

  .uf-process-title {
    font-size: 20px;
  }
}
</style>
